import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { makeStyles, mergeClasses } from '@fluentui/react-components';
import { ShellIcons, StatusDot, complianceTokenFor, shellMedia, shellMetrics, tokens, type ComplianceTone } from '@vigeo/ui';

/**
 * The dashboard's card chrome, as frame 06b `288:15` draws it.
 *
 * ONE PLACE, because the frame draws the same card five times: a bordered white surface at
 * `borderRadiusLarge` with 16px padding, a header of title / caption / optional link, and a stack
 * of hairline-divided rows beneath it. Five copies of that block is five chances for one card to
 * drift.
 *
 * THIS IS NOT THE CARD THE REST OF THE APP DRAWS, and the difference is deliberate rather than an
 * oversight. Elsewhere a card is `borderRadiusXLarge` + `shadowCard` + no border (checkpoint 7A,
 * Figma 249:6722). Frame 0.1 V4 — approved, and the source of truth for this row — draws
 * `borderRadiusLarge` + a `colorNeutralStroke2` hairline + no shadow, on every one of its cards.
 * Built as drawn; flagged in the PR so the same question can be settled for the other pages rather
 * than answered twice.
 */
const useStyles = makeStyles({
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    padding: tokens.spacingHorizontalL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    minWidth: 0,
  },
  // The whole `border` shorthand, not `borderColor`: Griffel's typed style rejects the colour
  // longhand on its own, and restating the shorthand is what `mergeClasses` overrides cleanly.
  cardCritical: { border: `${tokens.strokeWidthThin} solid ${tokens.colorComplianceCritical}` },
  hCritical: { color: tokens.colorComplianceCritical },
  head: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: tokens.spacingHorizontalM, flexWrap: 'wrap' },
  titles: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, minWidth: 0 },
  /** HR/Title 3 — 20/28 semibold. */
  h: { margin: 0, fontSize: tokens.fontSizeBase500, lineHeight: tokens.lineHeightBase500, fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1 },
  /** HR/Body 1 — 14/20 regular, secondary. */
  caption: { fontSize: tokens.fontSizeBase300, lineHeight: tokens.lineHeightBase300, color: tokens.colorNeutralForeground2 },
  action: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    color: tokens.colorBrandForegroundLink,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightSemibold,
    whiteSpace: 'nowrap',
    // 2.75rem is the accessible minimum touch target on a phone; desktop keeps the caption height.
    [shellMedia.belowTablet]: { minHeight: '2.75rem' },
  },
  rows: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', minWidth: 0 },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    minWidth: 0,
    ':not(:last-child)': { borderBottom: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}` },
    // The frame stacks the action under the text on a narrow card rather than squeezing both.
    [shellMedia.phone]: { alignItems: 'flex-start', flexWrap: 'wrap' },
  },
  rowRoomy: { paddingBlock: tokens.spacingVerticalM },
  rowTight: { paddingBlock: tokens.spacingVerticalS },
  rowText: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, flexGrow: 1, flexBasis: 0, minWidth: 0 },
  /** HR/Body 1 strong — 14/20 semibold. */
  rowTitle: { fontSize: tokens.fontSizeBase300, lineHeight: tokens.lineHeightBase300, fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1, overflowWrap: 'anywhere' },
  /** HR/Caption 1 — 12/16 regular, secondary. */
  rowDetail: { fontSize: tokens.fontSizeBase200, lineHeight: tokens.lineHeightBase200, color: tokens.colorNeutralForeground2, overflowWrap: 'anywhere' },
  /**
   * The frame's 36px tinted square. The FILL is `colorNeutralBackground2` rather than the frame's
   * `status/*-tint`: those tints exist in tokens.json with a light value only (#257), and this app
   * renders a dark theme too, so using them would paint a near-white square behind a dark card.
   * The severity still reads — it is carried by the icon's own colour, which is the compliance
   * token and themed.
   */
  icon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: '2.25rem',
    height: '2.25rem',
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
    color: tokens.colorNeutralForeground2,
  },
  empty: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase300, lineHeight: tokens.lineHeightBase300 },
  /**
   * The frame's secondary control at the end of a Waiting-on-HR row — drawn as a button, built as a
   * LINK, because every one of them goes to a page. Nothing on this dashboard changes anything, and
   * a viewer must find no control here that looks like it might.
   */
  rowAction: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    minHeight: shellMetrics.controlHeight,
    paddingInline: tokens.spacingHorizontalL,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightSemibold,
    whiteSpace: 'nowrap',
    textDecoration: 'none',
    ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
    [shellMedia.phone]: { minHeight: shellMetrics.touchTarget },
  },
  toneBadge: { color: tokens.colorNeutralForegroundOnBrand, fontWeight: tokens.fontWeightSemibold, flexShrink: 0 },
});

/**
 * A compliance-toned chip whose WORDS are the caller's.
 *
 * `StatusChip` renders a `RequirementStatus` and `ReadinessBadge` a `ReadinessState`; neither fits
 * a row whose label is a countdown ("In 16 days"). The tone still comes from `ComplianceTone`, so
 * this cannot invent a colour — only the sentence is new.
 */
export function ToneBadge({ tone, children }: { readonly tone: ComplianceTone; readonly children: ReactNode }) {
  return (
    <StatusDot color={complianceTokenFor[tone]}>{children}</StatusDot>
  );
}

/** The trailing control on a Waiting-on-HR row: the frame's secondary button, as a route link. */
export function RowAction({ label, to }: CardAction) {
  const styles = useStyles();
  return <Link to={to} className={styles.rowAction}>{label}</Link>;
}

export interface CardAction {
  readonly label: string;
  readonly to: string;
}

export interface DashboardCardShellProps {
  /** Identifies the heading the section is named by, so the card is one landmark with one name. */
  readonly id: string;
  readonly title: string;
  readonly caption?: ReactNode;
  readonly action?: CardAction;
  /**
   * The critical treatment frame `609:26466` draws on "Overdue updates": the card's edge and its
   * heading in the compliance-critical colour. A tone, not a colour, so it stays themed.
   */
  readonly tone?: 'critical';
  readonly children: ReactNode;
}

export function DashboardCardShell({ id, title, caption, action, tone, children }: DashboardCardShellProps) {
  const styles = useStyles();
  return (
    <section className={mergeClasses(styles.card, tone === 'critical' && styles.cardCritical)} aria-labelledby={id}>
      <div className={styles.head}>
        <div className={styles.titles}>
          <h2 id={id} className={mergeClasses(styles.h, tone === 'critical' && styles.hCritical)}>{title}</h2>
          {caption === undefined ? null : <span className={styles.caption}>{caption}</span>}
        </div>
        {/* Routed, never an anchor: an href here reloads the whole app to reach a page the router
            already has. */}
        {action === undefined ? null : (
          <Link to={action.to} className={styles.action}>
            {action.label}
            <ShellIcons.chevronRight />
          </Link>
        )}
      </div>
      {children}
    </section>
  );
}

/** The hairline-divided stack every card's body is. A list, because that is what it is. */
export function CardRows({ label, children }: { readonly label: string; readonly children: ReactNode }) {
  const styles = useStyles();
  return <ul className={styles.rows} aria-label={label}>{children}</ul>;
}

export interface CardRowProps {
  /** `roomy` is the Waiting-on-HR rhythm (12px); `tight` every other card's (8px). */
  readonly density?: 'roomy' | 'tight';
  /** A severity-coloured 36px square. Omitted entirely when the row has no icon. */
  readonly icon?: { readonly render: () => ReactNode; readonly tone?: ComplianceTone };
  readonly lead?: ReactNode;
  readonly title: ReactNode;
  readonly detail?: ReactNode;
  readonly trailing?: ReactNode;
}

export function CardRow({ density = 'tight', icon, lead, title, detail, trailing }: CardRowProps) {
  const styles = useStyles();
  return (
    // mergeClasses, never a template literal: Griffel class strings carry a sequence hash, and
    // concatenating two makes Fluent's own merge discard everything after the second one.
    <li className={mergeClasses(styles.row, density === 'roomy' ? styles.rowRoomy : styles.rowTight)}>
      {icon === undefined ? null : (
        <span className={styles.icon} style={icon.tone === undefined ? undefined : { color: complianceTokenFor[icon.tone] }} aria-hidden>
          {icon.render()}
        </span>
      )}
      {lead ?? null}
      <div className={styles.rowText}>
        <span className={styles.rowTitle}>{title}</span>
        {detail === undefined ? null : <span className={styles.rowDetail}>{detail}</span>}
      </div>
      {trailing ?? null}
    </li>
  );
}

/** What a card says when it has nothing to show. Never a zero dressed up as a row. */
export function CardEmpty({ children }: { readonly children: ReactNode }) {
  const styles = useStyles();
  return <p className={styles.empty}>{children}</p>;
}
